import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import type { ChannelSummary, Message, PublicUser } from '@itsappy/shared';
import {
  useForwardMessage,
  useInviteChannelMembers,
  useMessageEdits,
  useMessageToTask,
  useProjects,
} from '@/hooks/queries';
import { Avatar, Modal, Spinner } from '@/components/ui';
import { toast } from '@/store/ui';
import { cn } from '@/lib/utils';
import { Markdown } from './Markdown';

/* -------------------------------- forward -------------------------------- */

export function ForwardDialog({
  message,
  channelId,
  channels,
  onClose,
}: {
  message: Message | null;
  channelId: string;
  channels: ChannelSummary[];
  onClose: () => void;
}) {
  const forward = useForwardMessage(channelId);
  const [selected, setSelected] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [filter, setFilter] = useState('');

  const options = useMemo(() => {
    const query = filter.trim().toLowerCase();
    return channels
      .filter((channel) => channel.id !== channelId)
      .filter((channel) => !query || channel.name.toLowerCase().includes(query))
      .slice(0, 40);
  }, [channels, channelId, filter]);

  function reset() {
    setSelected([]);
    setNote('');
    setFilter('');
  }

  return (
    <Modal
      open={Boolean(message)}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Forward message"
      description="Send a copy somewhere else. The original stays where it is."
      footer={
        <>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!selected.length || forward.isPending}
            onClick={async () => {
              if (!message) return;
              try {
                await forward.mutateAsync({
                  messageId: message.id,
                  channelIds: selected,
                  note: note.trim() || undefined,
                });
                toast.success(
                  `Forwarded to ${selected.length} ${selected.length === 1 ? 'conversation' : 'conversations'}`,
                );
                reset();
                onClose();
              } catch (error) {
                toast.error(
                  'Could not forward',
                  error instanceof Error ? error.message : undefined,
                );
              }
            }}
          >
            {forward.isPending && <Spinner />}
            Forward
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {message && (
          <div className="max-h-28 overflow-y-auto rounded-lg bg-surface-2 p-2 text-xs text-slate-400">
            <span className="font-medium text-slate-300">{message.author.name}: </span>
            {message.body.slice(0, 300) || 'an attachment'}
          </div>
        )}

        <label>
          <span className="label">Add a note</span>
          <input
            className="input"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Optional"
          />
        </label>

        <div>
          <span className="label">Send to</span>
          <input
            className="input mb-2"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Search conversations"
          />
          <div className="max-h-56 space-y-1 overflow-y-auto">
            {options.map((channel) => (
              <label
                key={channel.id}
                className="flex min-h-10 cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-1.5 hover:bg-surface-2"
              >
                <input
                  type="checkbox"
                  className="accent-brand-500"
                  checked={selected.includes(channel.id)}
                  onChange={() =>
                    setSelected((current) =>
                      current.includes(channel.id)
                        ? current.filter((id) => id !== channel.id)
                        : [...current, channel.id],
                    )
                  }
                />
                <span className="truncate text-sm text-slate-200">
                  {channel.kind === 'dm' ? channel.name : `#${channel.name}`}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

/* ----------------------------- message → task ---------------------------- */

export function MessageToTaskDialog({
  message,
  channelId,
  onClose,
}: {
  message: Message | null;
  channelId: string;
  onClose: () => void;
}) {
  const { data: projects } = useProjects();
  const convert = useMessageToTask(channelId);
  const [projectId, setProjectId] = useState('');
  const [title, setTitle] = useState('');

  // Seed the title from the message the first time the dialog opens for it.
  const suggested = message?.body.replace(/\s+/g, ' ').trim().slice(0, 120) ?? '';
  const effectiveTitle = title || suggested;

  return (
    <Modal
      open={Boolean(message)}
      onClose={() => {
        setTitle('');
        onClose();
      }}
      title="Turn into a task"
      description="The task keeps a link back to this message and the conversation it came from."
      footer={
        <>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              setTitle('');
              onClose();
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!projectId || !effectiveTitle.trim() || convert.isPending}
            onClick={async () => {
              if (!message) return;
              try {
                const task = await convert.mutateAsync({
                  messageId: message.id,
                  projectId,
                  title: effectiveTitle.trim(),
                });
                toast.success('Task created', `#${task.number} ${task.title}`);
                setTitle('');
                onClose();
              } catch (error) {
                toast.error(
                  'Could not create the task',
                  error instanceof Error ? error.message : undefined,
                );
              }
            }}
          >
            {convert.isPending && <Spinner />}
            Create task
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <label>
          <span className="label">Project</span>
          <select
            className="input"
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
          >
            <option value="">Choose a project…</option>
            {projects?.items.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="label">Title</span>
          <input
            className="input"
            value={effectiveTitle}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="What needs doing?"
          />
        </label>
      </div>
    </Modal>
  );
}

/* ------------------------------ edit history ----------------------------- */

export function EditHistoryDialog({
  message,
  channelId,
  names,
  onClose,
}: {
  message: Message | null;
  channelId: string;
  names: Map<string, string>;
  onClose: () => void;
}) {
  const { data, isLoading } = useMessageEdits(channelId, message?.id);
  const versions = data?.edits ?? [];

  return (
    <Modal
      open={Boolean(message)}
      onClose={onClose}
      title="Edit history"
      description="Every earlier version of this message, oldest first."
    >
      {isLoading ? (
        <div className="grid h-24 place-items-center">
          <Spinner />
        </div>
      ) : versions.length === 0 ? (
        <p className="py-4 text-center text-sm text-slate-500">
          This message has not been edited.
        </p>
      ) : (
        <ol className="space-y-2">
          {versions.map((version, index) => (
            <li key={index} className="rounded-lg bg-surface-2 p-2.5">
              <p className="mb-1 text-[11px] text-slate-500">
                {format(new Date(version.editedAt), 'd MMM yyyy, HH:mm')}
              </p>
              <Markdown body={version.body} names={names} className="text-slate-400" />
            </li>
          ))}
          {data && (
            <li className={cn('rounded-lg p-2.5 ring-1 ring-brand-500/40')}>
              <p className="mb-1 text-[11px] text-brand-300">
                Current
                {data.editedAt && ` · ${format(new Date(data.editedAt), 'd MMM yyyy, HH:mm')}`}
              </p>
              <Markdown body={data.body} names={names} />
            </li>
          )}
        </ol>
      )}
    </Modal>
  );
}

/* --------------------------- outside mentions ----------------------------- */

/**
 * Intercepts a send that mentions people who cannot see this conversation.
 *
 * In a DM or private channel the server only badges channel members, so such
 * a mention renders as a chip but reaches no one — the author deserved to
 * find that out before sending, not never. Offers to add them first when the
 * channel and the author's role allow it.
 */
export function OutsideMentionDialog({
  users,
  channel,
  canInvite,
  onClose,
  onProceed,
}: {
  /** The mentioned people who are not in the conversation; empty when closed. */
  users: PublicUser[];
  channel: ChannelSummary;
  canInvite: boolean;
  onClose: () => void;
  /** Carry on with the send exactly as it was submitted. */
  onProceed: () => void;
}) {
  const invite = useInviteChannelMembers(channel.id);
  const isDm = channel.kind === 'dm';

  return (
    <Modal
      open={users.length > 0}
      onClose={onClose}
      title={
        users.length === 1
          ? `${users[0].name} isn't in this conversation`
          : "They aren't in this conversation"
      }
      description={
        isDm
          ? "Direct messages are only visible to the people in them, so a mention here won't notify them and they can't read it."
          : canInvite
            ? "This channel is private, so they won't be notified and can't read the message unless they're added. Added people can also read the channel's history."
            : "This channel is private, so they won't be notified and can't read the message. Only the channel creator or an admin can add people."
      }
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Go back
          </button>
          <button
            type="button"
            className={canInvite ? 'btn-secondary' : 'btn-primary'}
            onClick={onProceed}
          >
            Send anyway
          </button>
          {canInvite && (
            <button
              type="button"
              className="btn-primary"
              disabled={invite.isPending}
              onClick={async () => {
                try {
                  await invite.mutateAsync(users.map((member) => member.id));
                  toast.success(
                    users.length === 1
                      ? `${users[0].name} added to #${channel.name}`
                      : `${users.length} people added to #${channel.name}`,
                  );
                  onProceed();
                } catch (error) {
                  toast.error(
                    'Could not add them',
                    error instanceof Error ? error.message : undefined,
                  );
                }
              }}
            >
              {invite.isPending && <Spinner />}
              Add & send
            </button>
          )}
        </>
      }
    >
      <div className="space-y-1">
        {users.map((member) => (
          <div key={member.id} className="flex min-h-11 items-center gap-2.5 rounded-lg px-3 py-2">
            <Avatar user={member} size="sm" showStatus />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm text-slate-100">{member.name}</span>
              <span className="block truncate text-xs text-slate-500">{member.title}</span>
            </span>
          </div>
        ))}
      </div>
    </Modal>
  );
}
